import { createSlice } from '@reduxjs/toolkit';

const assetSlice = createSlice({
  name: 'asset',
  initialState: {
    assets: [],
    currentAsset: null,
    currentPage: 1,
    totalPages: 1,
    totalAssets: 0,
    loading: false,
    deleting: false,
    bulkDeleting: false,
    bulkCompletedAt: 0,
    bulkNotice: null,
    error: null,
    assetType: null, // Track current asset type filter
    // Server page the list view is on; every refetch (upload poll, post-delete) reuses it.
    pageQuery: { page: 1, limit: 10 },
  },
  reducers: {
    setAssetPageQuery: (state, action) => {
      state.pageQuery = action.payload;
    },
    getAssetsAction: (state, action) => {
      const nextType = action.payload?.type || null;
      const typeChanged = state.assetType !== nextType;
      if (typeChanged) {
        state.assets = [];
        state.totalAssets = 0;
        state.currentAsset = null;
      }
      // Same-type refetch with rows on screen stays silent (upload poll, post-delete refresh).
      state.loading = typeChanged || state.assets.length === 0;
      state.error = null;
      state.assetType = nextType;
      // Any new fetch retires the bulk delete result notice.
      state.bulkNotice = null;
    },
    getAssetsSuccess: (state, action) => {
      // Stale response for a different type filter than the one on screen - drop it.
      if ((action.payload?.requestedType || null) !== state.assetType) return;
      // Handle response structure: { success: true, data: [...], count: 2 }
      const responseData = action.payload?.data || [];
      state.assets = Array.isArray(responseData) ? responseData : [];
      state.totalAssets = action.payload?.total ?? action.payload?.count ?? state.assets.length;
      state.loading = false;
      state.error = null;
    },
    getAssetsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.assets = [];
    },
    getAssetByIdAction: (state) => {
      state.loading = true;
      state.error = null;
    },
    getAssetByIdSuccess: (state, action) => {
      state.currentAsset = action.payload?.data || action.payload;
      state.loading = false;
      state.error = null;
    },
    getAssetByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.currentAsset = null;
    },
    deleteAssetAction: (state, action) => {
      state.deleting = true;
      state.error = null;
      // Store assetType for refetch after deletion
      if (action.payload?.assetType) {
        state.assetType = action.payload.assetType;
      }
    },
    deleteAssetSuccess: (state) => {
      state.deleting = false;
      state.error = null;
    },
    deleteAssetFailure: (state, action) => {
      state.deleting = false;
      state.error = action.payload;
    },
    bulkDeleteAssetsAction: (state) => {
      state.bulkDeleting = true;
      state.error = null;
      state.bulkNotice = null;
    },
    bulkDeleteAssetsSuccess: (state, action) => {
      state.bulkDeleting = false;
      state.bulkCompletedAt += 1;
      state.bulkNotice = {
        deleted: action.payload?.deleted || [],
        blocked: action.payload?.blocked || [],
        missing: action.payload?.missing || [],
      };
    },
    bulkDeleteAssetsFailure: (state, action) => {
      state.bulkDeleting = false;
      state.error = action.payload;
    },
    clearAssetBulkNotice: (state) => {
      state.bulkNotice = null;
    },
    clearCurrentAsset: (state) => {
      state.currentAsset = null;
    },
  },
});

export const {
  setAssetPageQuery,
  getAssetsAction,
  getAssetsSuccess,
  getAssetsFailure,
  getAssetByIdAction,
  getAssetByIdSuccess,
  getAssetByIdFailure,
  deleteAssetAction,
  deleteAssetSuccess,
  deleteAssetFailure,
  bulkDeleteAssetsAction,
  bulkDeleteAssetsSuccess,
  bulkDeleteAssetsFailure,
  clearAssetBulkNotice,
  clearCurrentAsset,
} = assetSlice.actions;

export { assetSlice };
